import { html } from '@acme/b2b/utils';
import { GridDefinitionFactory } from '@b2b/common/factories/gridDefinition/types';
import { GridStateFactory } from '@b2b/common/factories/gridState/types';
import { ApiService } from '@b2b/common/services/api/types';
import { CalendarService } from '@b2b/common/services/calendar';
import { PermissionsService } from '@b2b/common/services/permissions';
import { UserPreferencesService } from '@b2b/common/services/userPreferences/types';
import { allSettled } from '@b2b/common/utils/all-settled';
import { CanSelectRowEvent, ColumnDef, extractACMEPayload, UIBModal } from '@b2b/types/core';
import { TODO } from '@b2b/types/core/utils';
import { StateService } from '@uirouter/angularjs';
import angular, { IQService, IScope, ITimeoutService } from 'angular';
import { isEventRowSelectable, EventSelectionEntity } from '../utils/event-selection.utils';

export interface OrdersCheckoutAddEventModalData {
  accountId: number | null;
  startTime: string | number | Date;
  membershipId: string | null;
  eventIds: string[];
}

OrdersCheckoutAddEventModalController.$inject = [
  '$scope',
  '$timeout',
  '$state',
  '$uibModalInstance',
  '$q',
  'ApiService',
  'HelpersService',
  'PermissionsService',
  'CalendarService',
  'GridDefinitionFactory',
  'GridStateFactory',
  'TemplatesHelpersService',
  'UserPreferencesService',
  'MODAL_DATA',
];

function OrdersCheckoutAddEventModalController(
  $scope: IScope & {
    ordersCheckoutAddEventModal: TODO;
    errors: TODO[];
    model: { events: TODO[] };
    init: () => void;
    hasErrors: () => void;
    trim: (value: string) => string;
    success: (eventIds: string[]) => void;
    done: () => void;
    cancel: () => void;
  },
  $timeout: ITimeoutService,
  $state: StateService,
  $uibModalInstance: UIBModal<TODO>,
  $q: IQService,
  ApiService: ApiService,
  HelpersService: TODO,
  PermissionsService: PermissionsService,
  CalendarService: CalendarService,
  GridDefinitionFactory: GridDefinitionFactory,
  GridStateFactory: GridStateFactory,
  TemplatesHelpersService: TODO,
  UserPreferencesService: UserPreferencesService,
  MODAL_DATA: OrdersCheckoutAddEventModalData,
) {
  $scope.errors = [];

  const ctrl = {
    model: [] as TODO[],
    gridDefinition: new GridDefinitionFactory(),
    gridState: new GridStateFactory(),

    startDate: new Date(),
    startTime: '',
    endTime: '',
    viewId: '',
    searchQuery: '',
    timeFormat: HelpersService.dateFormats.standardTime,

    isCSR: false,
    isLoaded: false,
    isSubmitting: false,

    preferencesLoaded: false,

    eventQueue: {},
    selectedRows: {},

    eventListGridName: $state.current.name + '.grid0',

    optionsOverride: {
      pageSize: 10,
      paginationPageSize: 10,
      paginationPageSizes: [10, 25],
      data: 'ordersCheckoutAddEventModal.model',
      useAccessibleGrid: true,
      noUnselect: false,
      multiSelect: true,
      enableRowSelection: true,
      canSelectRow: (options: CanSelectRowEvent<EventSelectionEntity>) => {
        return ctrl.isRowSelectable(options.row.entity);
      },
      columnDefs: [
        {
          field: 'startTime',
          width: 100,
          displayName: 'Start Time',
          cellTemplate: html`
            <div class="ui-grid-cell-contents text-center">
              <span data-ng-style="row.entity.colorCategoryStyle"></span> 
              <span>{{ row.entity.formattedStartTime }}</span>
              <span class="sr-only"> Event starts at {{ row.entity.formattedStartTime }}</span>
            </div>
          `,
        },
        {
          field: 'startDate',
          displayName: 'Date',
          width: 80,
          visible: false,
          cellFilter: 'date: "' + HelpersService.dateFormats.shortDate + '"',
          cellFormatter: function (item) {
            return CalendarService.toFormat(item.value, HelpersService.dateFormats.shortDate);
          },
        },
        {
          field: 'name',
          displayName: 'Event Name',
          visible: true,
        },
        {
          field: 'organization.name',
          displayName: 'Organization Name',
          visible: false,
        },
        {
          field: 'scheduleName',
          displayName: 'Schedule Name',
          visible: true,
        },
        {
          field: 'resourceRequestDisplay',
          displayName: 'Resources',
          visible: false,
          minWidth: 150,
          colId: 'requestSummary',
        },
        {
          field: 'sold',
          displayName: 'Sold',
          width: 60,
          visible: false,
        },
        {
          field: 'reserved',
          displayName: 'Reserved',
          width: 80,
          visible: false,
        },
        {
          field: 'available',
          displayName: 'Available',
          width: 100,
          visible: true,
        },
        {
          field: 'checkedIn',
          displayName: 'Checked In',
          width: 90,
          visible: false,
        },
      ] as ColumnDef[],
      rowClasses: "{ 'ui-grid-row-unselectable': !grid.appScope.isRowSelectable(row.entity) }",
      appScopeProvider: {
        isRowSelectable: (entity: EventSelectionEntity) => {
          return ctrl.isRowSelectable(entity);
        },
        onDataGridPaginate: (newPageNumber, newPageSize, sortField, sortDirection) => {
          ctrl.onDataGridPaginate(newPageNumber, newPageSize, sortField, sortDirection);
        },
        onDataGridSortChange: (grid, columns, page) => {
          ctrl.onDataGridSortChange(grid, columns, page);
        },
        onDataGridSelectionChange: (row, e) => {
          ctrl.onDataGridSelectionChange(row);
        },
      },
    },

    isRowSelectable: function (entity: EventSelectionEntity) {
      return isEventRowSelectable(entity, {
        eventIds: MODAL_DATA.eventIds,
        membershipId: MODAL_DATA.membershipId,
      });
    },

    init: function () {
      const startDate = ctrl.getStartDate(MODAL_DATA.startTime);

      ctrl.isCSR = PermissionsService.check('CSR');
      ctrl.viewId = $state.current.name ?? '';

      if (
        CalendarService.current.eventsStartTimeTracker &&
        CalendarService.current.eventsStartTimeTracker instanceof Date
      ) {
        ctrl.startDate = new Date(
          CalendarService.current.eventsStartTimeTracker.getFullYear(),
          CalendarService.current.eventsStartTimeTracker.getMonth(),
          CalendarService.current.eventsStartTimeTracker.getDate(),
          0,
          0,
          0,
        );
      } else {
        ctrl.startDate = startDate;
      }

      ctrl.startTime = CalendarService.toISO8601(ctrl.startDate);

      ctrl.gridDefinition.init(ctrl.eventListGridName, ctrl.optionsOverride, ctrl.viewId, true);

      $scope.$watch('ordersCheckoutAddEventModal.startDate', ctrl.onStartDateUpdate);
      $scope.$on('$destroy', $scope.$on('DataGrid::UserPreferencesLoaded', ctrl.initData));
    },

    initData: function (e, data) {
      const props = ctrl.gridDefinition.getGridState();
      ctrl.gridState.updateGridState({
        page: props.page,
        pageSize: props.pageSize,
        sortField: 'startTime',
        sortDirection: 'asc',
      });
      ctrl.search();
      ctrl.preferencesLoaded = true;
    },

    search: function () {
      ctrl.isLoaded = false;
      if (ctrl.gridDefinition.userPreferencesLoaded) {
        ctrl.getEvents();
      }
    },

    getStartDate: function (_date: string | number | Date) {
      const date = _date ? new Date(_date) : new Date();
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      return date;
    },

    getEvents: function () {
      ApiService.event
        .search({
          ...ctrl.gridState.getGridState(),
          startTime: CalendarService.toISO8601(ctrl.startTime),
          endTime: CalendarService.toISO8601(ctrl.endTime),
          saleChannel: ctrl.isCSR ? 'customerRep' : undefined,
        })
        .then((response) => {
          ctrl.model = ctrl.process(response.data.payload.list);

          ctrl.isLoaded = true;
          ctrl.gridDefinition.isLoaded = true;

          $timeout(() => {
            $scope.$broadcast('DataGrid::ResizeGrid', {
              name: ctrl.gridDefinition.options.gridName,
              datasetLength: ctrl.model.length,
              totalItems: response.data.payload.pagination.count,
            });
          }, 10);
        })
        .catch(() => {
          // noop
        });
    },

    onStartDateUpdate: function (n, o) {
      const startDate = ctrl.startDate;
      const endDate = new Date(ctrl.startDate);

      startDate.setHours(0);
      startDate.setMinutes(0);
      startDate.setSeconds(0);

      endDate.setHours(23);
      endDate.setMinutes(59);
      endDate.setSeconds(59);

      ctrl.startTime = CalendarService.toISO8601(startDate);
      ctrl.endTime = CalendarService.toISO8601(endDate);

      if (ctrl.preferencesLoaded) {
        ctrl.getEvents();
      }
    },

    hasEvents: function () {
      return $scope.model.events instanceof Array && $scope.model.events.length > 0;
    },

    handleEvent: function (event) {
      if (ctrl.eventQueue[event.id]) {
        delete ctrl.eventQueue[event.id];
      } else {
        ctrl.eventQueue[event.id] = event;
      }
    },

    process: function (events) {
      for (let i = 0, ii = events.length; i < ii; i++) {
        const startDate = CalendarService.parseDate(events[i].startTime);
        const endDate = CalendarService.parseDate(events[i].endTime);

        events[i].startDate = startDate;
        events[i].formattedEndTime = CalendarService.toFormat(endDate, HelpersService.dateFormats.standardTime);
        events[i].formattedStartTime = CalendarService.toFormat(startDate, HelpersService.dateFormats.standardTime);
        events[i].isCancelled = events[i].state && events[i].state === 'cancelled' ? true : false;
        events[i].resourceRequestDisplay = ctrl.getEventRequestsDisplay(events[i]);
        if (events[i].colorCategory && events[i].colorCategory.textColor) {
          events[i].colorCategoryStyle = TemplatesHelpersService.getColorCategoryStyle(events[i].colorCategory);
        }

        if (events[i].offSchedule) {
          events[i].scheduleName = 'None (from Order Request)';
        }

        if (events[i].manuallyDeleted) {
          events[i].available = events[i].available || 0;

          if (!events[i].sold) {
            events[i].sold = events[i].soldQuantity || 0;
          }

          if (!events[i].checkedIn) {
            events[i].checkedIn = events[i].checkInCount || 0;
          }
        }
      }

      return events;
    },

    getEventRequestsDisplay: function (event) {
      if (event.hasOwnProperty('requestSummary') && event.requestSummary.hasResourceRequests) {
        const personnelRequests = ctrl.getRequestsForCategory(event, 'people');
        const equipmentRequests = ctrl.getRequestsForCategory(event, 'equipment');
        const placesRequests = ctrl.getRequestsForCategory(event, 'place');

        if (personnelRequests || equipmentRequests || placesRequests) {
          return [personnelRequests, equipmentRequests, placesRequests].filter((request) => request).join(', ');
        }
      }

      return 'No requests';
    },

    getRequestsForCategory: function (event, category) {
      let categorySuffix;
      let fulfilled = 0;
      let total = 0;
      let requests;
      let r = '';

      if (category === 'place') {
        categorySuffix = 'Places';
      } else {
        categorySuffix = HelpersService.cap(category);
      }

      if (event.requestSummary.hasOwnProperty(category)) {
        fulfilled = event.requestSummary[category].fulfilledRequestsCount
          ? event.requestSummary[category].fulfilledRequestsCount
          : 0;
        total = event.requestSummary[category].totalRequestsCount
          ? event.requestSummary[category].totalRequestsCount
          : 0;
        requests = total ? fulfilled + '/' + total : '';
        // Show resource name if total and fulfilled is 1 and only 1
        if (fulfilled === total && fulfilled === 1) {
          if (
            event.requestSummary[category].resourceNames instanceof Array &&
            event.requestSummary[category].resourceNames[0]
          ) {
            r = event.requestSummary[category].resourceNames[0];
          }
        } else {
          r = requests ? requests + ' ' + categorySuffix : '';
        }
      }

      return r;
    },

    getEventIds: function (events) {
      const ids: string[] = [];

      angular.forEach(events, (_event) => {
        if (_event.selected) {
          ids.push(_event.data.id);
        }
      });

      return ids;
    },

    query: function ($event: Event) {
      $event.preventDefault();
      $event.stopPropagation();

      ctrl.gridState.setPropertyValue('search', ctrl.searchQuery);
      $scope.$broadcast('DataGrid::Search', { name: ctrl.eventListGridName });
    },

    onDataGridPaginate: function (newPage, pageSize, sortField, sortDirection) {
      ctrl.gridState.updateGridState({
        page: newPage,
        pageSize,
        sortField,
        sortDirection,
      });

      ctrl.search();
    },

    onDataGridSortChange: function (grid, columns, page) {
      const data = columns[0];
      ctrl.gridState.updateGridState({
        page,
        sortField: data.field,
        sortDirection: data.sort.direction,
      });
      ctrl.search();
    },

    onDataGridSelectionChange: function (row) {
      if (row instanceof Array) {
        for (let i = 0, ii = row.length; i < ii; i++) {
          ctrl.selectedRows[row[i].entity.id] = { selected: row[i].isSelected, data: row[i].entity };
          row[i].entity.active = row[i].isSelected;
        }
      } else if (row instanceof Object && row.isSelected !== undefined) {
        ctrl.selectedRows[row.entity.id] = { selected: row.isSelected, data: row.entity };
        row.entity.active = row.isSelected;
      }
    },
  };

  $scope.init = function () {
    ctrl.init();
  };

  $scope.hasErrors = function () {
    return $scope.errors.length > 0;
  };

  $scope.trim = function (value: string) {
    return value.replace(/\s+/g, '').toLowerCase();
  };

  $scope.done = function () {
    const eventIds = ctrl.getEventIds(ctrl.selectedRows);
    $scope.errors = [];

    if (eventIds.length) {
      $scope.success(eventIds);
    } else {
      $scope.errors.push({ message: 'No event selected. Select an event to continue.' });
    }
  };

  $scope.cancel = function () {
    $uibModalInstance.dismiss('cancel');
  };

  $scope.success = function (eventIds: string[]) {
    ctrl.isSubmitting = true;

    const getEventDetail = (eventId: string) => {
      if (MODAL_DATA.accountId != null) {
        return ApiService.partners.reseller.event({ accountId: MODAL_DATA.accountId as number, eventId });
      }

      if (MODAL_DATA.membershipId != null) {
        return ApiService.event.detail(eventId, { membershipId: MODAL_DATA.membershipId as string });
      }
      
      return ApiService.program.instances.detail(eventId);
    };

    const promises = eventIds.map(getEventDetail);

    allSettled($q, promises)
      .then((responses) => {
        const events = responses.map(extractACMEPayload());
        $uibModalInstance.close(events);
      })
      .catch(() => {
        $scope.errors.push({ message: 'Unable to retrieve event' });
      })
      .finally(() => {
        ctrl.isSubmitting = false;
      });
  };

  $scope.ordersCheckoutAddEventModal = ctrl;
  $scope.init();
}

export const OrdersCheckoutAddEventModalControllerModule = angular
  .module('acme.view.application.orders.checkout.addEventModal.controllers', [
    'acme.common.service.api',
    'acme.common.service.helpers',
    'acme.common.service.calendar',
    'acme.common.service.permissions',
    'acme.common.factory.gridState',
    'acme.common.factory.gridDefinition',
    'acme.common.service.templates.helpers',
  ])
  .controller('OrdersCheckoutAddEventModalController', OrdersCheckoutAddEventModalController).name;
